import { useState, useCallback, useRef } from 'react'
import Papa from 'papaparse'
import { FEE_MGMT_COLS_BY_READABLE, FEE_MGMT_COLUMNS } from '../constants/Columns'

export const useCSVProcessor = () => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [fileName, setFileName] = useState('')
  const lastProgressUpdateTimeRef = useRef(0)

  // Count commas in a string, ignoring commas within quotes
  const countCommas = (str) => {
    let count = 0
    let inQuotes = false

    for (let i = 0; i < str.length; i++) {
      // Toggle quote state
      if (str[i] === '"') {
        inQuotes = !inQuotes
      }

      // Count commas outside of quotes
      if (str[i] === ',' && !inQuotes) {
        count++
      }
    }

    return count
  }

  const processCsvWithPapa = useCallback((file, onComplete, onError) => {
    const startTime = Date.now()
    const processedRows = []
    let totalBytes = 0
    let headersSet = new Set()
    let duplicateColumns = []

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      chunkSize: 1024 * 1024, // 1MB chunks
      chunk: (results) => {
        // Only check for duplicate columns in the first chunk
        if (!headersSet.size) {
          // Find all duplicates in a single pass
          duplicateColumns = findDuplicateColumns(results.meta.fields)

          // If we found duplicates, stop processing
          if (duplicateColumns.length > 0) {
            setProgress(100)
            setIsProcessing(false)
            return onComplete({
              rows: [],
              totalRows: 0,
              fileName: file.name,
              processingTime: 0,
              headers: new Set(results.meta.fields),
              duplicateColumns,
            })
          }

          headersSet = new Set(results.meta.fields)
        }

        // Add row numbers to the chunk and map readable names to column keys
        const rowsToAdd = results.data.map((row, index) => {
          return {
            rowNumber: processedRows.length + index + 1,
            validationErrors: {},
            userMarkedForDelete: false,
            ...Object.fromEntries(
              Object.entries(row).map(([readableKey, value]) => {
                const columnKeyConstant = FEE_MGMT_COLS_BY_READABLE[readableKey]
                const mappedKey = columnKeyConstant ? FEE_MGMT_COLUMNS[columnKeyConstant].columnKey : readableKey

                // Only split and trim commas for fields that are configured to handle comma-separated values
                let trimmedValue = value
                if (typeof value === 'string') {
                  const columnConfig = columnKeyConstant ? FEE_MGMT_COLUMNS[columnKeyConstant] : null
                  const shouldSplitCommas = columnConfig?.context?.customGridColumnOptions?.shouldSplitCommaValues

                  if (shouldSplitCommas && value.includes(',')) {
                    // Split by comma, trim each part, and rejoin
                    trimmedValue = value
                      .split(',')
                      .map((part) => part.trim())
                      .filter((part) => part !== '') // Remove empty parts
                      .join(',')
                  } else {
                    // Single value, just trim normally (preserves spaces within quoted values)
                    trimmedValue = value.trim()
                  }
                }
                return [mappedKey, trimmedValue]
              }),
            ),
          }
        })

        processedRows.push(...rowsToAdd)
        totalBytes += results.data.length

        // Calculate progress and time metrics
        const progress = Math.min((totalBytes / (file.size / 50)) * 100, 100)
        const elapsedTime = (Date.now() - startTime) / 1000

        if (elapsedTime > 0) {
          const rowsPerSecond = totalBytes / elapsedTime
          const remainingRows = file.size / 50 - totalBytes
          const estimatedTimeRemaining = remainingRows / rowsPerSecond

          // Throttle progress updates to reduce re-renders
          const now = Date.now()
          if (now - lastProgressUpdateTimeRef.current > 100 || progress >= 99.9) {
            // Update every 100ms or at completion
            lastProgressUpdateTimeRef.current = now
            setProgress(progress)
            setTimeRemaining(estimatedTimeRemaining)
          }
        }
      },
      complete: () => {
        setIsProcessing(false)
        setProgress(100)
        const processingTime = ((Date.now() - startTime) / 1000).toFixed(1)
        onComplete({
          rows: processedRows,
          totalRows: processedRows.length,
          fileName: file.name,
          processingTime: Number(processingTime),
          headers: headersSet,
          duplicateColumns: duplicateColumns,
        })
      },
      error: (error) => {
        setIsProcessing(false)
        setProgress(0)
        onError(error)
      },
    })
  }, [])

  const processCSV = useCallback(
    async (file) => {
      if (!file) {
        throw new Error('No file provided')
      }

      setIsProcessing(true)
      setProgress(0)
      setTimeRemaining(null)
      setFileName(file.name)
      lastProgressUpdateTimeRef.current = 0

      return new Promise((resolve, reject) => {
        // Read the file as text first to validate comma counts before parsing
        const fileReader = new FileReader()

        fileReader.onload = (event) => {
          const csvText = event.target.result
          const lines = csvText.split(/\r?\n/).filter((line) => line.trim()) // Split and remove empty lines

          if (lines.length === 0) {
            setIsProcessing(false)
            setProgress(0)
            return reject(new Error('Empty CSV file'))
          }

          // Count commas in header row
          const headerCommaCount = countCommas(lines[0])

          // Collect all validation issues instead of returning early
          const validationIssues = {
            invalidCommaRows: [],
            duplicateColumns: [],
            headers: new Set(),
            rows: [],
            totalRows: 0,
            fileName: file.name,
            processingTime: 0,
          }

          // Check each data row for comma count
          for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
              // Skip empty lines
              const rowCommaCount = countCommas(lines[i])
              if (rowCommaCount !== headerCommaCount) {
                validationIssues.invalidCommaRows.push({
                  rowNumber: i + 1, // 1-based row numbers
                  lineContent: lines[i],
                  expectedCommas: headerCommaCount,
                  actualCommas: rowCommaCount,
                })
              }
            }
          }

          // Extract header row and check for duplicates
          const headerRow = lines[0]
          const headers = Papa.parse(headerRow, { header: false }).data[0]
          validationIssues.headers = new Set(headers)

          // Find duplicate headers
          validationIssues.duplicateColumns = findDuplicateColumns(headers)

          // If we have validation issues that prevent further processing, return now
          if (validationIssues.invalidCommaRows.length > 0) {
            setProgress(100)
            setIsProcessing(false)
            return resolve(validationIssues)
          }

          // Otherwise, continue with normal CSV parsing which will check for other issues
          processCsvWithPapa(
            file,
            (results) => {
              // Combine the validation results with Papa parsing results
              const combinedResults = {
                ...results,
                invalidCommaRows: validationIssues.invalidCommaRows,
                duplicateColumns: validationIssues.duplicateColumns || results.duplicateColumns,
              }
              resolve(combinedResults)
            },
            reject,
          )
        }

        fileReader.onerror = () => {
          setIsProcessing(false)
          setProgress(0)
          reject(new Error('Error reading file'))
        }

        fileReader.readAsText(file)
      })
    },
    [processCsvWithPapa],
  )

  // Find all duplicates in a single pass through the header array
  const findDuplicateColumns = (headers) => {
    const exactDuplicates = new Set()
    const normalizedMap = new Map()
    const result = []

    // First pass: find exact duplicates and build normalized map
    const headerCounts = {}

    headers.forEach((header) => {
      // Track exact duplicates
      headerCounts[header] = (headerCounts[header] || 0) + 1

      if (headerCounts[header] > 1) {
        exactDuplicates.add(header)
      }

      // Also track normalized version for case-insensitive duplicates
      const normalized = header.trim().toLowerCase()

      if (!normalizedMap.has(normalized)) {
        normalizedMap.set(normalized, [])
      }

      normalizedMap.get(normalized).push(header)
    })

    // Add all exact duplicates to the result
    exactDuplicates.forEach((header) => {
      result.push(header)
    })

    // Add case-insensitive duplicates that aren't already exact duplicates
    normalizedMap.forEach((headersWithSameNormalization) => {
      if (headersWithSameNormalization.length > 1) {
        // Add headers that aren't already included as exact duplicates
        headersWithSameNormalization.forEach((header) => {
          if (!exactDuplicates.has(header) && !result.includes(header)) {
            result.push(header)
          }
        })
      }
    })

    return result
  }

  const reset = useCallback(() => {
    setIsProcessing(false)
    setProgress(0)
    setTimeRemaining(null)
    setFileName('')
  }, [])

  const formatTimeRemaining = useCallback((seconds) => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s remaining`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return `${minutes}m ${remainingSeconds}s remaining`
  }, [])

  return {
    isProcessing,
    progress,
    timeRemaining,
    fileName,
    processCSV,
    reset,
    formatTimeRemaining,
    setFileName,
  }
}
